import { useEffect } from 'react';

const listenerCallbacks = new WeakMap();

let observer: IntersectionObserver;

function handleIntersections(entries: IntersectionObserverEntry[]) {
  entries.forEach((entry) => {
    if (listenerCallbacks.has(entry.target)) {
      const cb = listenerCallbacks.get(entry.target);

      if (entry.isIntersecting || entry.intersectionRatio > 0) {
        observer.unobserve(entry.target);
        listenerCallbacks.delete(entry.target);
        cb(entry.target);
      }
    }
  });
}

function getIntersectionObserver() {
  if (observer === undefined) {
    observer = new IntersectionObserver(handleIntersections, {
      rootMargin: '100px',
      threshold: 0.15,
    });
  }
  return observer;
}

interface IUseIntersection<T> {
  ref: React.RefObject<T>;
  callback: (target: T) => void;
}

export const useIntersection = <T extends HTMLElement>(
  props: IUseIntersection<T>
) => {
  const { ref, callback } = props;
  useEffect(() => {
    const target = ref.current;
    const observer = getIntersectionObserver();
    if (target) {
      listenerCallbacks.set(target, callback);
      observer.observe(target);
    }

    return () => {
      if (target) {
        listenerCallbacks.delete(target);
        observer.unobserve(target);
      }
    };
  }, [callback, ref]);
};
